import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import KeyboardDoubleArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import KeyboardDoubleArrowRightOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type JSX } from 'react';

import { findStepElement, HELP_PANEL_ATTR } from './dom';
import { HelpStepJump } from './HelpStepJump';
import { cardPosition, rectOf, type Rect } from './position';
import { uiText } from './text';
import type { HelpLanguage, HelpStep, PageHelp } from './types';
import { groupStepsBySection, localizedSection, localizedStep } from './types';

/**
 * The guided tour: a spotlight over the real page, stepping through the guide's steps.
 *
 * Hand-rolled rather than driver.js/Shepherd — it needs a rectangle, a card and two buttons, and
 * those libraries fight exactly what this needs: staying inside the open dialog, keeping Escape
 * from closing the dialog underneath, a counter that is a button, section skipping.
 *
 * The caller passes ONLY steps that are currently on screen; this component re-checks each one as
 * it advances, because a step can disappear mid-tour (a row refreshing, a toast collapsing) and a
 * spotlight on nothing is worse than a skipped step.
 *
 * It is not only linear: the step counter opens a searchable jump list, the section arrows skip a
 * whole block, and the card hands over to click-to-explain.
 */

const CARD_WIDTH = 360;
/** Space to reserve for the card when deciding whether it fits below the target. */
const CARD_HEIGHT = 200;

interface HelpTourProps {
  steps: HelpStep[];
  /**
   * The subtree the tour is confined to — the open dialog, when the tour was started over one.
   * Every lookup is scoped to it, so a step whose anchor also exists behind the backdrop resolves
   * to the copy the user is actually looking at.
   */
  scope?: HTMLElement | null;
  /** Where to enter the walk. The drawer's "start here" and the inspector both pick a step. */
  startIndex?: number;
  /** The guide — for the section order and the translated section names. */
  help: PageHelp;
  language: HelpLanguage;
  /** Hands over to click-to-explain; the tour ends. Omitted, the button is not offered. */
  onInspect?: () => void;
  onClose: () => void;
}

export const HelpTour = ({
  steps,
  scope = null,
  startIndex = 0,
  help,
  language,
  onInspect,
  onClose,
}: HelpTourProps): JSX.Element | null => {
  const [index, setIndex] = useState(startIndex);
  const [rect, setRect] = useState<Rect | null>(null);
  /** The step counter, while it is acting as the jump list's anchor. */
  const [jumpAnchor, setJumpAnchor] = useState<HTMLElement | null>(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const text = uiText(language);
  const spoken = step ? localizedStep(step, language) : null;

  const goTo = useCallback(
    (next: number): void => {
      if (next < 0 || next >= steps.length) return;
      setIndex(next);
    },
    [steps.length],
  );

  const groups = useMemo(() => groupStepsBySection(help.sections, steps), [help.sections, steps]);
  const groupIndex = groups.findIndex((group) => step && group.steps.includes(step));

  /** Skip to the first step of the neighbouring section — a block at a time, not a step. */
  const skipSection = (delta: number): void => {
    const target = groups[groupIndex + delta]?.steps[0];
    if (target) goTo(steps.indexOf(target));
  };

  // Measure before paint so the spotlight never renders one frame in the wrong place.
  useLayoutEffect(() => {
    if (!step) return;
    // A detached scope means the dialog was closed mid-tour; nothing inside it can be found, so the
    // tour walks off the end and closes rather than spotlighting the page behind it.
    const lookup = scope ?? document;
    const el = findStepElement(step.selector, lookup);
    // Setting state here is the point of a layout effect: the target is measured (or found gone)
    // after React committed the DOM and before the browser paints, so the spotlight never shows a
    // frame in the wrong place — the case React's docs give for `useLayoutEffect` + setState.
    if (!el) {
      if (isLast) onClose();
      // eslint-disable-next-line react-hooks/set-state-in-effect -- skip a vanished step before paint
      else setIndex((i) => i + 1);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setRect(rectOf(el));

    // Smooth scroll moves the target for ~40 frames; follow it, then on any later reflow.
    let frames = 0;
    let raf = 0;
    const track = (): void => {
      const current = findStepElement(step.selector, lookup);
      if (current) setRect(rectOf(current));
      if (++frames < 40) raf = window.requestAnimationFrame(track);
    };
    raf = window.requestAnimationFrame(track);

    const remeasure = (): void => {
      const current = findStepElement(step.selector, lookup);
      if (current) setRect(rectOf(current));
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
  }, [step, isLast, onClose, scope]);

  const jumpOpen = jumpAnchor !== null;

  useEffect(() => {
    /**
     * Capture phase, and the handled keys are stopped here. A tour started over a dialog sits ON
     * TOP of it and both listen for Escape — without capture, ending the tour also dismissed the
     * half-filled form it was explaining. While the jump list is open, Escape closes only the list
     * and the arrows are left to the caret in its search box.
     */
    const onKey = (e: KeyboardEvent): void => {
      if (jumpOpen) {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        e.preventDefault();
        setJumpAnchor(null);
        return;
      }
      if (e.key !== 'Escape' && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.stopPropagation();
      e.preventDefault();
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') goTo(index + 1);
      else goTo(index - 1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [goTo, index, jumpOpen, onClose]);

  if (!step || !rect) return null;
  const card = cardPosition(rect, { width: CARD_WIDTH, height: CARD_HEIGHT });
  const title = spoken?.title ?? step.title;

  return (
    <Portal>
      {/* One box with an enormous spread shadow: dims and blocks the whole page while leaving the
          target itself untouched and readable. */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          borderRadius: 1,
          zIndex: (t) => t.zIndex.modal + 20,
          boxShadow: '0 0 0 9999px rgba(31, 47, 69, 0.55)',
          outline: '2px solid #F39200',
          outlineOffset: 2,
          pointerEvents: 'auto',
          transition: 'top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease',
        }}
      />
      <Box
        role="dialog"
        // Excluded from dialog detection: the tour's own card must never be taken for the work
        // surface a later tour should confine itself to.
        {...{ [HELP_PANEL_ATTR]: 'tour' }}
        aria-label={text.tourAria(index + 1, steps.length, title)}
        sx={{
          position: 'fixed',
          top: card.top,
          left: card.left,
          width: CARD_WIDTH,
          zIndex: (t) => t.zIndex.modal + 21,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 12px 32px rgba(31, 47, 69, 0.28)',
          p: 2,
        }}
      >
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {/* The counter is the way OUT of the linear walk, so it has to look pressable. */}
          <ButtonBase
            onClick={(event) => setJumpAnchor(event.currentTarget)}
            data-help="help-tour-jump"
            aria-label={text.tourJumpAria(index + 1, steps.length)}
            aria-haspopup="dialog"
            sx={{ borderRadius: 1, px: 0.5, py: 0.25, ml: -0.5, '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: 'primary.main' }}>
              {text.tourCounter(index + 1, steps.length)}
            </Typography>
            <ExpandMoreOutlinedIcon sx={{ fontSize: 15, color: 'primary.main', ml: 0.25 }} />
          </ButtonBase>

          <Stack direction="row" sx={{ alignItems: 'center', mr: -0.75 }}>
            {groups.length > 1 && (
              <>
                <Tooltip title={text.previousSection}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={groupIndex <= 0}
                      onClick={() => skipSection(-1)}
                      aria-label={text.previousSection}
                    >
                      <KeyboardDoubleArrowLeftOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={text.nextSection}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={groupIndex < 0 || groupIndex >= groups.length - 1}
                      onClick={() => skipSection(1)}
                      aria-label={text.nextSection}
                    >
                      <KeyboardDoubleArrowRightOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <IconButton size="small" onClick={onClose} aria-label={text.endTour}>
              <CloseOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Stack>

        {step.section && (
          <Typography
            noWrap
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: 'text.disabled',
              mt: 0.25,
            }}
          >
            {localizedSection(help, step.section, language)}
          </Typography>
        )}

        <Typography sx={{ fontSize: 14.5, fontWeight: 600, mt: 0.5 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}>
          {spoken?.body ?? step.body}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 1.75, alignItems: 'center' }}>
          {onInspect && (
            <Button
              size="small"
              color="inherit"
              startIcon={<TouchAppOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={onInspect}
              data-help="help-tour-inspect"
              sx={{ fontSize: 11.5, color: 'text.secondary', ml: -0.5 }}
            >
              {text.pointAtField}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" color="inherit" onClick={() => goTo(index - 1)} disabled={index === 0}>
            {text.back}
          </Button>
          <Button size="small" variant="contained" onClick={() => (isLast ? onClose() : goTo(index + 1))}>
            {isLast ? text.done : text.next}
          </Button>
        </Stack>
      </Box>

      <HelpStepJump
        anchorEl={jumpAnchor}
        steps={steps}
        help={help}
        language={language}
        currentIndex={index}
        onPick={goTo}
        onClose={() => setJumpAnchor(null)}
      />
    </Portal>
  );
};
